import { supabase } from '@/core/supabase/client';

/**
 * Cierre mensual.
 *
 * El motor vive entero en PostgreSQL (`close_period` / `reopen_period`): valida
 * los invariantes ANTES de escribir nada y congela el período en una sola
 * transacción. Desde acá sólo se lo invoca y se leen sus resultados.
 */

export interface ClosureVersion {
  version_id: string;
  version_no: number;
  is_current: boolean;
  closed_at: string;
  closed_by: string | null;
  closed_by_name: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
}

export interface Closure {
  closure_id: string;
  period_year: number;
  period_month: number;
  status: 'open' | 'closed';
  current_version_id: string | null;
  versions: ClosureVersion[]; /**
   * Cuándo dejó de estar al día, o `null` si refleja los trabajos tal como
   * están.
   *
   * Liquidar ya no congela los trabajos sin facturar, así que entre el cálculo
   * y la factura puede cambiar cualquier cosa. Mostrar una liquidación vieja
   * como vigente es peor que no mostrarla: los números se leen y se usan.
   */
  stale_since?: string | null;
}

export interface SettlementEntry {
  participant_id: string;
  display_name: string;
  earned_honoraria_cents: number;
  earned_mobility_cents: number;
  adjustments_cents: number;
  billed_cents: number;
  balance_cents: number;
}

export interface Transfer {
  from_participant_id: string;
  from_name: string;
  to_participant_id: string;
  to_name: string;
  amount_cents: number;
}

export interface ClosureDetail {
  version_id: string;
  version_no: number;
  is_current: boolean;
  closed_at: string;
  closed_by_name: string | null;
  reopened_at: string | null;
  reopen_reason: string | null;
  closure_id: string;
  organization_id: string;
  period_year: number;
  period_month: number;
  status: 'open' | 'closed';
  is_admin: boolean;
  /** Un miembro común recibe SÓLO su propia línea (D1). */
  entries: SettlementEntry[];
  /** Un miembro común recibe sólo las transferencias en las que interviene. */
  transfers: Transfer[];
  snapshot: { works_count: number; total_kg: number; total_cents: number };
}

export async function listClosures(organizationId: string): Promise<Closure[]> {
  const { data, error } = await supabase.rpc('list_closures', { p_org: organizationId });
  if (error) throw error;
  return (data ?? []) as Closure[];
}

export async function fetchClosureDetail(versionId: string): Promise<ClosureDetail> {
  const { data, error } = await supabase.rpc('closure_detail', { p_version: versionId });
  if (error) throw error;
  return data as ClosureDetail;
}

/** Devuelve el id de la versión creada. Falla entero si algún invariante no da. */
export async function closePeriod(
  organizationId: string,
  year: number,
  month: number,
): Promise<string> {
  const { data, error } = await supabase.rpc('close_period', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return data as string;
}

/**
 * Reabrir el período para poder corregirlo.
 *
 * El motivo es OPCIONAL. Lo era obligatorio y lo que conseguía era que se
 * escribiera «error»: quien reabre es la misma persona que acaba de decidir
 * hacerlo, en su propia veterinaria, casi siempre para arreglar algo suyo.
 * Pedirle que lo justifique por escrito es ponerle un peaje a corregirse.
 *
 * Lo que sí queda registrado, y es lo que sirve: quién reabrió y cuándo.
 */
export async function reopenPeriod(closureId: string, reason?: string): Promise<void> {
  const { error } = await supabase.rpc('reopen_period', {
    p_closure: closureId,
    p_reason: reason?.trim() || null,
  });
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// Ajustes (D3)
// ---------------------------------------------------------------------------

export interface AdjustmentEntry {
  participant_id: string;
  display_name: string;
  amount_cents: number;
}

export interface AdjustmentBatch {
  batch_id: string;
  concept: string;
  reason: string;
  created_at: string;
  entries: AdjustmentEntry[];
}

export interface AdjustmentInput {
  organizationId: string;
  year: number;
  month: number;
  participantId: string;
  /** Ausente = contra la cuenta técnica de la organización. */
  counterpartParticipantId?: string | null;
  amountCents: number;
  concept: string;
  reason: string;
}

/**
 * Registra un ajuste BALANCEADO. La base escribe siempre las dos patas, así que
 * no existe forma de mandar un asiento desbalanceado desde acá.
 */
export async function createAdjustment(input: AdjustmentInput): Promise<string> {
  const { data, error } = await supabase.rpc('create_adjustment', {
    p: {
      organization_id: input.organizationId,
      billing_year: input.year,
      billing_month: input.month,
      participant_id: input.participantId,
      counterpart_participant_id: input.counterpartParticipantId ?? null,
      amount_cents: input.amountCents,
      concept: input.concept,
      reason: input.reason,
    },
  });
  if (error) throw error;
  return data as string;
}

/**
 * Borrar un ajuste.
 *
 * No existía, y su ausencia dejaba a un ajuste mal tipeado sin salida: no se
 * podía editar, no se podía borrar, y la única corrección era construir a mano
 * el asiento inverso invirtiendo las dos patas —con un formulario que ni
 * siquiera acepta importes negativos—. Para un precio mal escrito, eso es pedir
 * contabilidad de partida doble a alguien que se equivocó tipeando.
 *
 * Los renglones del ajuste caen con él: su clave foránea ya estaba en cascada.
 */
export async function deleteAdjustment(batchId: string): Promise<void> {
  const { error } = await supabase.rpc('delete_adjustment', { p_batch: batchId });
  if (error) throw error;
}

export async function listAdjustments(
  organizationId: string,
  year: number,
  month: number,
): Promise<AdjustmentBatch[]> {
  const { data, error } = await supabase.rpc('list_adjustments', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return (data ?? []) as AdjustmentBatch[];
}

// ---------------------------------------------------------------------------
// Qué impide cerrar el período (UX-12)
// ---------------------------------------------------------------------------

export interface ClosingBlocker {
  work_session_id: string;
  work_date: string;
  client_id: string | null;
  client_name: string;
  establishment_name: string | null;
  services: string | null;
  reason: 'sin_precio' | 'sin_facturador';
}

export const MOTIVOS_DE_BLOQUEO: Record<ClosingBlocker['reason'], string> = {
  sin_precio: 'Sin precio de referencia para el período',
  sin_facturador: 'Sin facturador asignado',
};

/**
 * Los trabajos que impiden cerrar, con datos que una persona reconoce.
 *
 * La base ya los rechaza y explica por qué, pero enumerándolos por UUID: se
 * entendía el motivo y no se podía hacer nada, porque un UUID no se busca.
 * Esta consulta no agrega ninguna regla, llama a las mismas que usa
 * `close_period`.
 */
export async function listClosingBlockers(
  organizationId: string,
  year: number,
  month: number,
): Promise<ClosingBlocker[]> {
  const { data, error } = await supabase.rpc('closing_blockers', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return (data ?? []) as ClosingBlocker[];
}

/**
 * Lo que la organización le debe a cada participante en el período.
 *
 * Hay clientes que no pagan contra factura: llevan el efectivo. Esa plata entra
 * a la organización y **no es ganancia suya**: queda debiéndosela a quien hizo
 * el trabajo. Sin mostrarlo, el saldo de la organización queda colgado y no se
 * entiende cuánto de eso le toca a cada uno.
 *
 * Se deriva trabajo por trabajo en la base —reparto del honorario más movilidad
 * de quien viajó— y no se guarda en ningún campo nuevo.
 */
export interface DeudaDeLaOrganizacion {
  participant_id: string;
  display_name: string;
  amount_cents: number;
}

export async function fetchOrganizationDebt(
  organizationId: string,
  year: number,
  month: number,
): Promise<DeudaDeLaOrganizacion[]> {
  const { data, error } = await supabase.rpc('organization_debt', {
    p_org: organizationId,
    p_year: year,
    p_month: month,
  });
  if (error) throw error;
  return (data ?? []) as DeudaDeLaOrganizacion[];
}
